import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "@/hooks/useSafeTranslation";
import {
  LayoutDashboard,
  Zap,
  Link2,
  BarChart2,
  CreditCard,
  Users,
  Gift,
  Share2,
  TrendingUp,
  Bell,
  LifeBuoy,
  Shield,
  Settings,
  Menu,
  X,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/useAuth";
import { useUsage } from "@/hooks/useUsage";
import { useProfile } from "@/hooks/useAdmin";
import { PlanBadge } from "@/components/ui/PlanBadge";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { cn, toDisplayString, toSafeReactNode } from "@/lib/utils";

type AppLayoutProps = {
  children: ReactNode;
};

function NavGroup({
  title,
  items,
}: {
  title: unknown;
  items: ReadonlyArray<{ to: string; label: unknown; icon: typeof LayoutDashboard }>;
}) {
  return (
    <div className="space-y-1">
      <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
        {toDisplayString(title)}
      </p>
      {items.map(({ to, label, icon: Icon }) => (
        <Link
          key={to}
          to={to}
          className={cn(
            "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
            "text-text-muted hover:bg-surface-2 hover:text-text",
            "[&.active]:bg-accent/10 [&.active]:text-accent [&.active]:font-medium"
          )}
          activeProps={{ className: "active" }}
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          {toDisplayString(label)}
        </Link>
      ))}
    </div>
  );
}

function SidebarContent({
  onNavClick,
  email,
  planId,
  isAdmin,
  onSignOut,
  isSigningOut,
}: {
  onNavClick?: () => void;
  email: string | null;
  planId: string | undefined;
  isAdmin: boolean;
  onSignOut: () => void;
  isSigningOut: boolean;
}) {
  const { t } = useTranslation("common");
  const mainNav = [
    { to: "/", label: toDisplayString(t("nav.overview")), icon: LayoutDashboard },
    { to: "/leads", label: toDisplayString(t("nav.leads")), icon: Zap },
    { to: "/crm", label: toDisplayString(t("nav.crm")), icon: Link2 },
  ];
  const accountNav = [
    { to: "/usage", label: toDisplayString(t("nav.usage")), icon: BarChart2 },
    { to: "/billing", label: toDisplayString(t("nav.billing")), icon: CreditCard },
    { to: "/team", label: toDisplayString(t("nav.team")), icon: Users },
    { to: "/settings", label: toDisplayString(t("nav.settings")), icon: Settings },
  ];
  const moreNav = [
    { to: "/referrals", label: toDisplayString(t("nav.referrals")), icon: Gift },
    { to: "/affiliate", label: toDisplayString(t("nav.affiliate")), icon: Share2 },
    { to: "/lead-signals", label: toDisplayString(t("nav.leadSignals")), icon: TrendingUp },
    { to: "/reminders", label: toDisplayString(t("nav.reminders")), icon: Bell },
    { to: "/support", label: toDisplayString(t("nav.support")), icon: LifeBuoy },
  ];
  return (
    <>
      <Link
        to="/"
        className="flex items-center gap-2 px-3 py-4 border-b border-border"
        onClick={onNavClick}
      >
        <img src="/icon.svg" alt="" width={28} height={28} className="shrink-0" />
        <img src="/wordmark.svg" alt={toDisplayString(t("login.title"))} className="h-6 w-auto" />
      </Link>
      <nav className="flex flex-1 flex-col gap-6 overflow-y-auto p-3">
        <NavGroup title={toSafeReactNode(t("navGroup.navigation"))} items={mainNav} />
        <NavGroup title={toSafeReactNode(t("navGroup.account"))} items={accountNav} />
        <NavGroup title={toSafeReactNode(t("navGroup.more"))} items={moreNav} />
        {isAdmin && (
          <div className="space-y-1">
            <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-text-muted">
              {toDisplayString(t("admin.title"))}
            </p>
            <Link
              to="/admin"
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                "text-text-muted hover:bg-surface-2 hover:text-text",
                "[&.active]:bg-accent/10 [&.active]:text-accent [&.active]:font-medium"
              )}
              activeProps={{ className: "active" }}
              onClick={onNavClick}
            >
              <Shield className="h-4 w-4 shrink-0" aria-hidden />
              {toDisplayString(t("admin.title"))}
            </Link>
          </div>
        )}
      </nav>
      <div className="border-t border-border p-3 space-y-2">
        <div className="flex flex-wrap gap-2 px-2 text-[10px] text-text-muted">
          <Link to="/privacy" className="hover:underline">{toDisplayString(t("footer.privacy"))}</Link>
          <span>·</span>
          <Link to="/terms" className="hover:underline">{toDisplayString(t("footer.terms"))}</Link>
          <span>·</span>
          <Link to="/pricing" className="hover:underline">{toDisplayString(t("footer.pricing"))}</Link>
          <span>·</span>
          <Link to="/lawful-use" className="hover:underline">{toDisplayString(t("footer.lawfulUse"))}</Link>
          <span>·</span>
          <Link to="/impressum" className="hover:underline">{toDisplayString(t("footer.impressum"))}</Link>
        </div>
        <p className="truncate px-2 text-xs text-text-muted" title={toDisplayString(email) || undefined}>
          {toDisplayString(email ?? t("common.noEmail"))}
        </p>
        <div className="flex items-center gap-2">
          {planId != null && <PlanBadge planId={planId} />}
        </div>
        <button
          type="button"
          onClick={onSignOut}
          disabled={isSigningOut}
          className="w-full rounded-md px-3 py-2 text-left text-sm text-text-muted hover:bg-surface-2 hover:text-text disabled:opacity-60 disabled:cursor-not-allowed"
          aria-busy={isSigningOut}
        >
          {isSigningOut ? toDisplayString(t("common.signingOut")) : toDisplayString(t("nav.signOut"))}
        </button>
      </div>
    </>
  );
}

export function AppLayout({ children }: AppLayoutProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state, signOut } = useAuth();
  const { data: usage } = useUsage();
  const { data: profile } = useProfile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await signOut();
      queryClient.clear();
      navigate({ to: "/login", replace: true });
    } finally {
      setIsSigningOut(false);
    }
  }, [signOut, navigate, queryClient, isSigningOut]);

  useEffect(() => {
    if (state.status === "unauthenticated") {
      navigate({ to: "/login", replace: true });
    }
  }, [state.status, navigate]);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const openSidebar = useCallback(() => setSidebarOpen(true), []);

  useEffect(() => {
    if (!sidebarOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeSidebar();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [sidebarOpen, closeSidebar]);

  useEffect(() => {
    if (sidebarOpen) {
      closeButtonRef.current?.focus();
    } else if (wasOpenRef.current) {
      menuButtonRef.current?.focus();
    }
    wasOpenRef.current = sidebarOpen;
  }, [sidebarOpen]);

  const email = state.status === "authenticated" ? state.email : null;
  const isAdmin = profile?.isAdmin === true;

  return (
    <div className="flex h-screen overflow-hidden bg-bg">
      {/* Desktop sidebar */}
      <aside className="hidden w-[240px] shrink-0 flex-col border-r border-border bg-surface md:flex">
        <SidebarContent
          email={email}
          planId={usage?.planId}
          isAdmin={isAdmin}
          onSignOut={handleSignOut}
          isSigningOut={isSigningOut}
        />
      </aside>

      {/* Mobile sidebar: kein motion/react (AnimatePresence) — weniger Konflikte mit React-19-Reconciliation. */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-bg/80 backdrop-blur-sm md:hidden"
            onClick={closeSidebar}
            aria-hidden
          />
          <aside
            className="fixed left-0 top-0 z-50 flex h-full w-[240px] flex-col border-r border-border bg-surface md:hidden"
            role="dialog"
            aria-modal="true"
            aria-label={toDisplayString(t("navGroup.navigation"))}
          >
            <div className="flex items-center justify-end border-b border-border p-2">
              <button
                ref={closeButtonRef}
                type="button"
                onClick={closeSidebar}
                className="rounded p-2 text-text-muted hover:bg-surface-2 hover:text-text"
                aria-label={toDisplayString(t("common.closeSidebar"))}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarContent
              onNavClick={closeSidebar}
              email={email}
              planId={usage?.planId}
              isAdmin={isAdmin}
              onSignOut={handleSignOut}
              isSigningOut={isSigningOut}
            />
          </aside>
        </>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex shrink-0 items-center gap-2 border-b border-border bg-surface px-4 py-3 md:hidden">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={openSidebar}
            className="rounded p-2 text-text-muted hover:bg-surface-2 hover:text-text"
            aria-label={toDisplayString(t("common.openMenu"))}
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <img src="/icon.svg" alt="" width={24} height={24} />
            <img src="/wordmark.svg" alt={toDisplayString(t("login.title"))} className="h-5 w-auto" />
          </Link>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-5xl w-full">
            <ErrorBoundary>
              {children}
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
